import { type FC, type PropsWithChildren } from 'react'

import { EVM, Solana, UTXO, createConfig } from '@lifi/sdk'
import { WalletManagementProvider } from '@lifi/wallet-management'
import { WalletManagementConfig } from '@lifi/wallet-management'

import { QUICKNODE_RPCS } from '@src/configs/rpc'
import { defaultCoinbaseConfig } from '@src/configs/wallet/coinbase'
import { defaultMetaMaskConfig } from '@src/configs/wallet/metaMask'
import { defaultWalletConnectConfig } from '@src/configs/wallet/walletConnect'
import { useWalletMenuModifier } from '@src/hooks/useWalletMenuModifier'
// Import both providers for conditional usage
import { AbstractEVMProvider } from '@src/providers/Wallet/EVMProvider'
import { SVMProvider } from '@src/providers/Wallet/SVMProvider'
import { StandardEVMProvider } from '@src/providers/Wallet/StandardEVMProvider'
import { UTXOProvider } from '@src/providers/Wallet/UTXOProvider'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('Web3ModalProvider')

const WalletMenuProvider: FC<PropsWithChildren> = ({ children }) => {
    const config: WalletManagementConfig = {
        locale: 'en',
        metaMask: defaultMetaMaskConfig,
        coinbase: defaultCoinbaseConfig,
        walletConnect: defaultWalletConnectConfig,
    }

    // Apply custom modifications to the wallet menu
    useWalletMenuModifier()

    return <WalletManagementProvider config={config}>{children}</WalletManagementProvider>
}

export function Web3ModalProvider({ children }: React.PropsWithChildren) {
    // Check if we're in a local environment to avoid CORS issues
    const currentEnv = process.env.REACT_APP_FEATURE_GATE_ENV?.toUpperCase() || ''
    const isLocalEnvironment = currentEnv.startsWith('LOCAL_')

    // Initialize LIFI SDK configuration
    try {
        createConfig({
            apiKey: process.env.REACT_APP_LIFI_API_KEY,
            apiUrl: process.env.REACT_APP_LIFI_API_URL,
            providers: [EVM(), Solana(), UTXO()],
            integrator: 'mach.exchange',
            rpcUrls: QUICKNODE_RPCS,
            preloadChains: !isLocalEnvironment, // Disable preloading in local environments to avoid CORS issues
        })
    } catch (error) {
        logger.error('Failed to initialize LiFi SDK configuration:', error)
        // Continue rendering the app even if LiFi SDK fails to initialize
    }

    // Conditionally use either AbstractEVMProvider or StandardEVMProvider based on feature flag
    const isAbstractConnectorEnabled = isFeatureEnabled('ABSTRACT_CONNECTOR')

    // Choose the appropriate EVM provider
    const ActiveEVMProvider = isAbstractConnectorEnabled ? AbstractEVMProvider : StandardEVMProvider

    return (
        <ActiveEVMProvider>
            <UTXOProvider>
                <SVMProvider>
                    <WalletMenuProvider>{children}</WalletMenuProvider>
                </SVMProvider>
            </UTXOProvider>
        </ActiveEVMProvider>
    )
}
